import {Socket} from "ngx-socket-io";
import {Injectable} from "@angular/core";
import {BehaviorSubject, Observable} from "rxjs";
import {GlobalConfig} from "../configs/GlobalConfig";
import {UserTokensRepository} from "../repositories/user-tokens.repository";
import {AppEventBus} from "../event-buses/app.event-bus";
import {ClientRepository} from "../repositories/client.repository";
import {EventName} from "../interfaces/websocket/EventName";
import {UserSessionRepository} from "../repositories/user-session.repository";
import {Authentication} from "../interfaces/websocket/outbound/Authentication";
import {Company} from "../interfaces/rest/Company";
import {AppEvent} from "../event-buses/AppEvent";
import {AppEventName} from "../event-buses/AppEventName";
import {AppStatusEvent} from "../events/AppStatusEvent";
import {Unauthenticated} from "../interfaces/websocket/inbound/Unauthenticated";
import {Unauthorized} from "../interfaces/websocket/inbound/Unauthorized";

@Injectable({
  providedIn: 'root'
})
export class SocketIoService extends Socket {

  private eventNames: Set<string> = new Set();
  private readySubject: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(private userTokensRepository: UserTokensRepository,
              private clientRepository: ClientRepository,
              private userSessionRepository: UserSessionRepository,
              private appEventBus: AppEventBus) {
    super({
      url: GlobalConfig.socketIoServerUrl,
      options: {
        transports: ['websocket'],
        transportOptions: {
          'websocket': {
            secure: true,
          }
        }
      }
    });
  }

  override connect(): any {
    console.log('Attempt to connect to SocketIO server');
    this.disconnect();

    this.on(EventName.Inbound.connect, (data: any) => {
      console.log('Connected to SocketIO server. Initiating authentication');

      const currentUser = this.userSessionRepository.getUserSession()!;
      const authentication = new Authentication();
      authentication.userName = currentUser.email!;
      authentication.token = this.userTokensRepository.getUserTokens().accessToken;
      authentication.internalUser = currentUser.userType === 'see';

      if(authentication.internalUser) {
        this.emit(EventName.Outbound.authentication, authentication);
      }
      else {
        this.clientRepository.getClient(Number(currentUser.clientId!)).subscribe({
          next: (client: Company) => {
            if(!client) {
              this.emit(EventName.Outbound.authentication, authentication);
            }
            else {
              authentication.clientName = client.companyName!;
              this.emit(EventName.Outbound.authentication, authentication);
            }
          },
          error: (error) => {
            this.emit(EventName.Outbound.authentication, authentication);
          }
        });
      }
    });

    this.on(EventName.Inbound.disconnect, (data: any) => {
      console.log(`Disconnected from SocketIO server`);

      this.readySubject.next(false);
      this.appEventBus.dispatch(new AppEvent(AppEventName.AddAppStatus, new AppStatusEvent('dataWebsocketConnection', 't.reestablishDataConnection', 1)));

      setTimeout(() => this.connect(), 5000);
    });

    this.on(EventName.Inbound.authenticated, (data: any) => {
      this.readySubject.next(true);
      this.appEventBus.dispatch(new AppEvent(AppEventName.RemoveAppStatus, new AppStatusEvent('dataWebsocketConnection', 't.reestablishDataConnection', 1)));
    });

    this.on(EventName.Inbound.unauthenticated, (data: any) => {
      const unauthenticated: Unauthenticated = data as Unauthenticated;
      console.log(`Failed to authenticate with SocketIO server. Reason: ${unauthenticated.message}`);

      setTimeout(() => {
        // Refresh access token and try again.
        this.userTokensRepository.getAccessToken().subscribe({
          next: (data) => {
            this.connect();
          },
          error: (error) => {
            console.log(`Failed to get access token to authenticate with SocketIO server`);
            this.connect();
          }
        });
      }, 5000);
    });

    this.on(EventName.Inbound.unauthorized, (data: any) => {
      const unauthorized: Unauthorized = data as Unauthorized;
      console.log(`Server responded with unauthorized message: ${unauthorized.message}`);
    });

    super.connect();
  }

  override on(eventName: string, callback: Function): void {
    this.eventNames.add(eventName);
    super.on(eventName, callback);
  }

  override disconnect(close?: any): any {
    console.log('Attempt to disconnect from SocketIO server');

    this.eventNames.forEach((eventName) => {
      this.removeAllListeners(eventName);
    });

    this.eventNames.clear();
    super.disconnect(close);

    this.readySubject.next(false);
    this.appEventBus.dispatch(new AppEvent(AppEventName.AddAppStatus, new AppStatusEvent('dataWebsocketConnection', 't.reestablishDataConnection', 1)));
  }

  getReadyObservable(): Observable<boolean> {
    return this.readySubject.asObservable();
  }

  isReady(): boolean {
    return this.readySubject.getValue();
  }
}
